import axios from "axios";
import FileDropzone from "../../components/FileDropzone";
import { useEffect, useState } from "react";
import { FileText, ShieldCheck, Sparkles, PackageCheck, RotateCcw, CheckCircle2 } from "lucide-react";
import { API, abs, money } from "./shared";
import { useAuth } from "../../auth/AuthContext";
import { RevisionTracker, useRevisionSummary, payForRevision, DesignDisputePanel } from "./DesignRevisions";

// ============================================================
// PHASE C · Proofing panel — drafts, revisions, comments, approve
// ============================================================
export function ProofingPanel({ brief, role, userId, onChange }) {
  const [drafts, setDrafts] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [comments, setComments] = useState([]);
  const [uploading, setUploading] = useState([]);
  const [draftNote, setDraftNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [revNote, setRevNote] = useState("");
  const [commentBody, setCommentBody] = useState("");
  const isAgency = role === "agency" && brief.awarded_agency_id === userId;
  const isBuyer = role === "buyer" && brief.buyer_id === userId;
  const isAdmin = role === "admin";
  const readOnly = brief.status === "approved" || brief.dispute_status === "open" || brief.status === "closed";
  const { user } = useAuth();
  const [summary, loadSummary] = useRevisionSummary(brief.id, [brief.status, brief.proofing_stage, brief.revision_round]);
  const needsPay = !!summary?.next_revision_requires_payment;

  const load = async () => {
    try {
      const [d, c] = await Promise.all([
        axios.get(`${API}/design/briefs/${brief.id}/drafts`),
        axios.get(`${API}/design/briefs/${brief.id}/comments`),
      ]);
      setDrafts(d.data.drafts || []);
      setCurrentRound(d.data.current_round || 0);
      setComments(c.data.comments || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load proofing data");
    }
  };
  useEffect(() => { load(); }, [brief.id, brief.status, brief.revision_round]); // eslint-disable-line

  const latestDraft = drafts[0];
  const stageLabel = brief.proofing_stage === "awaiting_agency_redo" ? "Awaiting agency redo"
    : brief.proofing_stage === "awaiting_buyer_review" ? "Awaiting buyer review"
    : brief.proofing_stage === "approved" ? "Approved"
    : currentRound === 0 ? "Awaiting first draft" : "In proofing";

  const submitDraft = async () => {
    if (uploading.length === 0) { setErr("Attach at least one draft file"); return; }
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/design/briefs/${brief.id}/drafts`, {
        file_ids: uploading.map(f => f.id),
        note: draftNote || null,
      });
      setUploading([]); setDraftNote("");
      await load(); onChange();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit draft");
    } finally { setBusy(false); }
  };

  const requestRevision = async () => {
    if (revNote.trim().length < 3) { setErr("Please describe what to change (min 3 chars)"); return; }
    setBusy(true); setErr("");
    try {
      if (needsPay) {
        if (!window.confirm(`All ${summary.free_revisions} free revisions are used. Pay ${money(summary.revision_rate_inr)} into escrow for the next revision round?`)) { setBusy(false); return; }
        await payForRevision(brief, user);
      }
      await axios.post(`${API}/design/briefs/${brief.id}/revisions`, { note: revNote });
      setRevNote("");
      await load(); await loadSummary(); onChange();
    } catch (e) {
      if (e?.code === "USER_CANCELLED") setErr("Payment cancelled — revision not requested.");
      else setErr(e?.description || e.response?.data?.detail || "Could not request revision");
      loadSummary();
    } finally { setBusy(false); }
  };

  const approve = async () => {
    if (!window.confirm(`Approve round ${currentRound}? This unlocks final delivery.`)) return;
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/design/briefs/${brief.id}/approve`);
      await load(); onChange();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not approve");
    } finally { setBusy(false); }
  };

  const postComment = async () => {
    if (commentBody.trim().length < 1) return;
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/design/briefs/${brief.id}/comments`, { body: commentBody });
      setCommentBody("");
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not post comment");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="proofing-panel" style={{marginTop:20}}>
      <div className="drawer-section-title">
        <h3>Proofing loop</h3>
        <span data-testid="proofing-stage">{stageLabel} · round {currentRound || 0}</span>
      </div>

      {brief.awarded_agency_id && <RevisionTracker summary={summary}/>}

      {err && <div style={{padding:10,margin:"6px 0",background:"#FEF2F2",border:"1px solid #FCA5A5",color:"#991B1B",borderRadius:6,fontSize:12}} data-testid="proofing-err">{err}</div>}

      {/* Drafts list */}
      {drafts.length === 0 ? (
        <div className="pipeline-empty" data-testid="proofing-no-drafts">
          {isAgency ? "Upload your first draft to start the proofing loop." : "Waiting for the agency to submit the first draft."}
        </div>
      ) : (
        <div className="draft-list" data-testid="draft-list">
          {drafts.map(d => (
            <div key={d.id} className={`draft-card ${d.status}`} data-testid={`draft-card-${d.id}`}>
              <div className="draft-head">
                <b>Round {d.revision_round}</b>
                <span className={`status ${d.status === "approved" ? "completed" : d.status === "revision_requested" ? "matching" : "live"}`} data-testid={`draft-status-${d.id}`}>{d.status.replace(/_/g," ")}</span>
                <em style={{fontSize:11,color:"#94a3b8"}}>{new Date(d.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</em>
              </div>
              {d.note && <p style={{color:"#475569",fontSize:12,margin:"6px 0"}}>{d.note}</p>}
              <div className="draft-thumbs" data-testid={`draft-thumbs-${d.id}`}>
                {(d.file_urls || []).map((u, i) => {
                  const isImg = /\.(png|jpe?g|webp|gif)$/i.test(d.file_names?.[i] || "");
                  return isImg ? (
                    <a key={i} href={abs(u)} target="_blank" rel="noreferrer" className="draft-thumb">
                      <img src={abs(u)} alt={d.file_names?.[i] || `draft ${i}`} onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                    </a>
                  ) : (
                    <a key={i} href={abs(u)} target="_blank" rel="noreferrer" className="asset-row" style={{gridTemplateColumns:"32px 1fr auto"}}>
                      <div className="asset-icon"><FileText size={14}/></div>
                      <div className="asset-info"><b>{d.file_names?.[i] || `file ${i+1}`}</b><span>Preview</span></div>
                      <span className="asset-download">↗</span>
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Agency — upload draft */}
      {isAgency && !readOnly && (latestDraft?.status !== "submitted") && (
        <div className="proof-row" data-testid="agency-draft-uploader">
          <p className="dz-label" style={{margin:"14px 0 6px",font:"10px 'DM Mono'",color:"#6e7e93",textTransform:"uppercase",letterSpacing:.5}}>
            Upload {currentRound === 0 ? "first draft" : `round ${currentRound + 1}`} · watermarked preview until buyer approves
          </p>
          <FileDropzone
            testid="draft-file-dropzone"
            scope="design_draft"
            jobId={null}
            briefId={brief.id}
            accept="image/*,application/pdf,.ai,.eps,.zip"
            value={uploading}
            onChange={setUploading}
            label="Add draft file(s)"
            hint="PNG · JPG · PDF · AI · EPS — up to 100 MB each"
          />
          <textarea
            data-testid="draft-note-input"
            rows={2}
            value={draftNote}
            onChange={e => setDraftNote(e.target.value)}
            placeholder="Note to buyer (optional) — e.g. Round 2 addresses colour palette feedback"
            style={{width:"100%",font:"13px Outfit",padding:10,border:"1px solid var(--line)",borderRadius:5,marginTop:8}}
          />
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
            <button data-testid="draft-submit-button" className="primary-btn" onClick={submitDraft} disabled={busy || uploading.length === 0}>
              {busy ? "Uploading…" : `Submit round ${currentRound + 1}`} <Sparkles size={15}/>
            </button>
          </div>
        </div>
      )}

      {/* Buyer — request revision / approve */}
      {isBuyer && !readOnly && latestDraft?.status === "submitted" && (
        <div className="proof-row" data-testid="buyer-proof-actions" style={{marginTop:14,padding:14,border:"1px solid var(--line)",borderRadius:8,background:"#F8FAFC"}}>
          <b style={{font:"600 13px Outfit"}}>Review round {currentRound}</b>
          {summary && (
            <span data-testid="revision-next-hint" style={{display:"block",fontSize:12,color: needsPay ? "#B45309" : "#64748b",marginTop:4}}>
              {needsPay ? `Free revisions used up — next revision costs ${money(summary.revision_rate_inr)} (paid into escrow).` : `${summary.free_remaining} free revision${summary.free_remaining === 1 ? "" : "s"} left.`}
            </span>
          )}
          <textarea
            data-testid="revision-note-input"
            rows={2}
            value={revNote}
            onChange={e => setRevNote(e.target.value)}
            placeholder="What should change? Colour, typography, layout, copy…"
            style={{width:"100%",font:"13px Outfit",padding:10,border:"1px solid var(--line)",borderRadius:5,marginTop:8}}
          />
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:10}}>
            <button data-testid="request-revision-button" className="secondary-btn" onClick={requestRevision} disabled={busy || revNote.trim().length < 3}>
              {needsPay ? `Pay ${money(summary.revision_rate_inr)} & request revision` : "Request revision"} <RotateCcw size={14}/>
            </button>
            <button data-testid="approve-draft-button" className="primary-btn" onClick={approve} disabled={busy}>
              Approve round {currentRound} <CheckCircle2 size={15}/>
            </button>
          </div>
        </div>
      )}

      {brief.awarded_agency_id && <DesignDisputePanel brief={brief} role={role} userId={userId} onChange={onChange}/>}

      {/* Approval state */}
      {brief.status === "approved" && (
        <div className="drawer-callout ok" data-testid="proof-approved-callout" style={{background:"#ECFDF5",border:"1px solid #6EE7B7",padding:14,borderRadius:8,display:"flex",gap:10,alignItems:"flex-start",marginTop:14}}>
          <CheckCircle2 size={16}/>
          <div>
            <b>Round {currentRound} approved · deliverables unlocked</b>
            <span style={{display:"block",fontSize:12,color:"#065F46",marginTop:4}}>Pre-press audit + one-click Convert to Print Order ships in Phase D.</span>
          </div>
        </div>
      )}

      {/* Comments thread */}
      <div className="drawer-section-title" style={{marginTop:20}}>
        <h3>Comments</h3>
        <span>{comments.length}</span>
      </div>
      <div className="comment-thread" data-testid="comment-thread">
        {comments.length === 0 ? (
          <div className="pipeline-empty">No messages yet.</div>
        ) : comments.map(c => (
          <div key={c.id} className={`comment ${c.author_role} ${c.kind || "message"}`} data-testid={`comment-${c.id}`}>
            <div className="comment-head">
              <b>{c.author_name}</b>
              <em>{c.author_role}</em>
              {c.kind && c.kind !== "message" && <span className="comment-tag">{c.kind}</span>}
              <span className="comment-time">{new Date(c.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
            </div>
            <p>{c.body}</p>
          </div>
        ))}
      </div>
      {(isBuyer || isAgency || isAdmin) && brief.status !== "closed" && (
        <div className="comment-composer" data-testid="comment-composer" style={{display:"flex",gap:8,marginTop:10}}>
          <input
            data-testid="comment-input"
            value={commentBody}
            onChange={e => setCommentBody(e.target.value)}
            placeholder="Send a note to the other party…"
            style={{flex:1,font:"13px Outfit",padding:10,border:"1px solid var(--line)",borderRadius:5}}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); postComment(); } }}
          />
          <button data-testid="comment-send-button" className="primary-btn" onClick={postComment} disabled={busy || !commentBody.trim()}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// PHASE D · Deliverables + pre-flight audit + Convert to print order
// ============================================================
export const REQUIRED_EXTS = ["ai", "pdf", "png"];

export function DeliveryPanel({ brief, role, userId, onChange }) {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({ quantity: 500, stock: "Uncoated 300 gsm", finishes: "None", ship_to: "", bid_window: 12, override_preflight: false });
  const isAgency = role === "agency" && brief.awarded_agency_id === userId;
  const isBuyer = role === "buyer" && brief.buyer_id === userId;
  const isAdmin = role === "admin";

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/design/briefs/${brief.id}/deliverables`);
      setData(d);
    } catch (e) { setErr(e.response?.data?.detail || "Failed to load deliverables"); }
  };
  useEffect(() => { load(); }, [brief.id, brief.status]); // eslint-disable-line

  const submitDeliverables = async () => {
    if (uploading.length === 0) { setErr("Attach at least one file"); return; }
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/design/briefs/${brief.id}/deliverables`, { file_ids: uploading.map(f => f.id) });
      setUploading([]);
      await load(); onChange();
    } catch (e) { setErr(e.response?.data?.detail || "Could not submit deliverables"); }
    finally { setBusy(false); }
  };

  const runPreflight = async () => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/design/briefs/${brief.id}/preflight`);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "Preflight failed"); }
    finally { setBusy(false); }
  };

  const convert = async () => {
    if (!convertForm.ship_to || convertForm.ship_to.trim().length < 3) { setErr("Enter a valid ship-to address"); return; }
    if (!window.confirm(`Convert this brief into a print order (${convertForm.quantity} units)? Final PDF will auto-attach.`)) return;
    setBusy(true); setErr("");
    try {
      const { data: d } = await axios.post(`${API}/design/briefs/${brief.id}/convert-to-job`, convertForm);
      setConvertOpen(false);
      alert(`Print order created: ${d.job_id}`);
      onChange();
    } catch (e) { setErr(e.response?.data?.detail || "Convert failed"); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const report = data.preflight_report;
  const complete = data.complete;
  const missing = data.missing_extensions || REQUIRED_EXTS;

  return (
    <div data-testid="delivery-panel" style={{marginTop:20}}>
      <div className="drawer-section-title">
        <h3>Final deliverables</h3>
        <span data-testid="delivery-checklist">
          {REQUIRED_EXTS.map(ext => (
            <span key={ext} className={`ext-chip ${(data.extensions || []).includes(ext) ? "ok" : "missing"}`} data-testid={`ext-chip-${ext}`}>
              {(data.extensions || []).includes(ext) ? "✓" : "○"} .{ext}
            </span>
          ))}
        </span>
      </div>
      {err && <div style={{padding:10,margin:"6px 0",background:"#FEF2F2",border:"1px solid #FCA5A5",color:"#991B1B",borderRadius:6,fontSize:12}} data-testid="delivery-err">{err}</div>}

      {(data.deliverables || []).length > 0 && (
        <div className="asset-list" data-testid="deliverables-list">
          {data.deliverables.map(f => (
            <a key={f.file_id} href={abs(f.url)} target="_blank" rel="noreferrer" className="asset-row" data-testid={`deliverable-${f.ext}`}>
              <div className="asset-icon"><FileText size={16}/></div>
              <div className="asset-info"><b>{f.filename}</b><span>.{f.ext} · {(f.size / 1024).toFixed(0)} KB</span></div>
              <span className="asset-download">Open ↗</span>
            </a>
          ))}
        </div>
      )}

      {/* Agency — upload final bundle */}
      {isAgency && brief.status !== "delivered" && (
        <div className="proof-row" data-testid="agency-final-uploader" style={{marginTop:12}}>
          <p className="dz-label" style={{margin:"6px 0 6px",font:"10px 'DM Mono'",color:"#6e7e93",textTransform:"uppercase",letterSpacing:.5}}>
            Upload final bundle · needs {missing.length ? missing.map(m => `.${m}`).join(" + ") : "all three formats already attached"}
          </p>
          <FileDropzone
            testid="final-file-dropzone"
            scope="design_draft"
            briefId={brief.id}
            accept=".ai,application/pdf,image/png,image/jpeg,application/postscript"
            value={uploading}
            onChange={setUploading}
            label="Add .AI + .PDF + .PNG"
            hint="Adobe Illustrator source + press-ready PDF + low-res PNG preview"
          />
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
            <button data-testid="final-submit-button" className="primary-btn" onClick={submitDeliverables} disabled={busy || uploading.length === 0}>
              {busy ? "Uploading…" : "Attach as deliverables"} <PackageCheck size={15}/>
            </button>
          </div>
        </div>
      )}

      {/* Preflight report */}
      {(data.deliverables || []).length > 0 && (
        <div className="preflight-box" data-testid="preflight-box" style={{marginTop:14,padding:14,border:"1px solid var(--line)",borderRadius:8,background:"#F8FAFC"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
            <div>
              <b style={{font:"600 13px Outfit"}}>Pre-press audit</b>
              <span style={{display:"block",font:"11px 'DM Mono'",color:"#64748b",marginTop:2}}>
                Checks: 300 DPI minimum · CMYK colour mode · {3}mm bleed on each edge
              </span>
            </div>
            {(isAgency || isBuyer || isAdmin) && brief.status !== "delivered" && (
              <button data-testid="run-preflight-button" className="secondary-btn" onClick={runPreflight} disabled={busy}>
                {report ? "Re-run audit" : "Run pre-flight audit"} <ShieldCheck size={14}/>
              </button>
            )}
          </div>
          {report && (
            <div style={{marginTop:10}}>
              <span className={`status ${report.pass ? "completed" : "matching"}`} data-testid="preflight-verdict">
                {report.pass ? "✓ Preflight passed" : `⚠ ${report.failures.length} issue(s)`}
              </span>
              {!report.pass && (
                <ul className="preflight-fails" data-testid="preflight-fails" style={{margin:"8px 0 0",padding:"8px 12px",listStyle:"none",background:"#FEF9C3",border:"1px solid #FDE68A",borderRadius:6,fontSize:12,color:"#78350F"}}>
                  {report.failures.map((f, i) => (
                    <li key={i}>
                      <b>{f.file}</b> · {f.check}: {f.detail.value !== undefined ? String(f.detail.value) : JSON.stringify(f.detail)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Convert to print order */}
      {isBuyer && brief.status === "approved" && (
        <div className="proof-row" data-testid="convert-cta" style={{marginTop:14}}>
          {!convertOpen ? (
            <button
              data-testid="convert-open-button"
              className="primary-btn full"
              onClick={() => setConvertOpen(true)}
              disabled={!complete}
              title={!complete ? `Missing ${missing.map(m=>`.${m}`).join(", ")}` : ""}
            >
              {complete ? "Convert to print order" : `Waiting for ${missing.map(m=>`.${m}`).join(" + ")}`} <PackageCheck size={16}/>
            </button>
          ) : (
            <div style={{padding:14,border:"1px solid #93C5FD",background:"#F0F6FF",borderRadius:8}}>
              <b style={{font:"600 13px Outfit"}}>Convert to print order</b>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
                <label style={{font:"10px 'DM Mono'",color:"#6e7e93",display:"flex",flexDirection:"column",gap:4,textTransform:"uppercase"}}>Quantity
                  <input data-testid="convert-quantity" type="number" min="1" value={convertForm.quantity} onChange={e => setConvertForm({...convertForm, quantity: Number(e.target.value)})} style={{font:"13px Outfit",padding:8,border:"1px solid var(--line)",borderRadius:5,textTransform:"none"}}/>
                </label>
                <label style={{font:"10px 'DM Mono'",color:"#6e7e93",display:"flex",flexDirection:"column",gap:4,textTransform:"uppercase"}}>Stock
                  <input data-testid="convert-stock" value={convertForm.stock} onChange={e => setConvertForm({...convertForm, stock: e.target.value})} style={{font:"13px Outfit",padding:8,border:"1px solid var(--line)",borderRadius:5,textTransform:"none"}}/>
                </label>
                <label style={{font:"10px 'DM Mono'",color:"#6e7e93",display:"flex",flexDirection:"column",gap:4,textTransform:"uppercase"}}>Finishes
                  <input data-testid="convert-finishes" value={convertForm.finishes} onChange={e => setConvertForm({...convertForm, finishes: e.target.value})} style={{font:"13px Outfit",padding:8,border:"1px solid var(--line)",borderRadius:5,textTransform:"none"}}/>
                </label>
                <label style={{font:"10px 'DM Mono'",color:"#6e7e93",display:"flex",flexDirection:"column",gap:4,textTransform:"uppercase"}}>Bid window (hrs)
                  <input data-testid="convert-bid-window" type="number" min="4" max="24" value={convertForm.bid_window} onChange={e => setConvertForm({...convertForm, bid_window: Number(e.target.value)})} style={{font:"13px Outfit",padding:8,border:"1px solid var(--line)",borderRadius:5,textTransform:"none"}}/>
                </label>
                <label style={{gridColumn:"span 2",font:"10px 'DM Mono'",color:"#6e7e93",display:"flex",flexDirection:"column",gap:4,textTransform:"uppercase"}}>Ship to
                  <input data-testid="convert-ship-to" value={convertForm.ship_to} onChange={e => setConvertForm({...convertForm, ship_to: e.target.value})} placeholder="City · warehouse · pin code" style={{font:"13px Outfit",padding:8,border:"1px solid var(--line)",borderRadius:5,textTransform:"none"}}/>
                </label>
                <label style={{gridColumn:"span 2",display:"flex",alignItems:"center",gap:6,font:"12px Outfit",color:"#475569"}}>
                  <input data-testid="convert-override" type="checkbox" checked={convertForm.override_preflight} onChange={e => setConvertForm({...convertForm, override_preflight: e.target.checked})}/>
                  Override pre-flight failures (admin discretion)
                </label>
              </div>
              <div style={{display:"flex",gap:8,marginTop:12,justifyContent:"flex-end"}}>
                <button className="secondary-btn" onClick={() => setConvertOpen(false)}>Cancel</button>
                <button data-testid="convert-submit-button" className="primary-btn" onClick={convert} disabled={busy}>
                  {busy ? "Creating…" : "Create print order"} <PackageCheck size={15}/>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {brief.status === "delivered" && data.linked_job_id && (
        <div className="drawer-callout ok" data-testid="convert-done-callout" style={{background:"#ECFDF5",border:"1px solid #6EE7B7",padding:14,borderRadius:8,display:"flex",gap:10,alignItems:"flex-start",marginTop:14}}>
          <PackageCheck size={16}/>
          <div>
            <b>Print order created · <span data-testid="linked-job-id">{data.linked_job_id}</span></b>
            <span style={{display:"block",fontSize:12,color:"#065F46",marginTop:4}}>Final PDF auto-attached — the auction goes live once admin releases the RFQ.</span>
          </div>
        </div>
      )}
    </div>
  );
}
